import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  Stat,
  useToast,
} from "../../design-system";
import EmployeeMultiPicker from "../../Components/Kronaly/Empleados/EmployeeMultiPicker";
import {
  getAbsencesUsageReport,
  getEmployees,
} from "../../helpers/kronaly_helper";

/**
 * Reporte de uso de ausencias.
 *
 * Capability `absences:approve`. Devuelve por empleado el breakdown de
 * días aprobados por tipo (vacation, personal, sick, parental, other)
 * dentro del rango pedido, más el balance vigente de vacaciones (opcional).
 *
 * CSV client-side: una columna por tipo + total + balance disponible.
 */
const TYPE_LABELS = {
  vacation: "Vacaciones",
  personal: "Personales",
  sick: "Baja",
  parental: "Parental",
  other: "Otras",
};

export function AbsencesReport({ isManager }) {
  const toast = useToast();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const firstOfYear = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
  }, []);

  const [startDate, setStartDate] = useState(firstOfYear);
  const [endDate, setEndDate] = useState(today);
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [includeBalance, setIncludeBalance] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isManager) return;
    getEmployees()
      .then((res) => {
        const list = res?.data || res || [];
        setEmployees(Array.isArray(list) ? list : []);
      })
      .catch(() => setEmployees([]));
  }, [isManager]);

  const run = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        include_balance: includeBalance ? 1 : 0,
      };
      if (selectedIds.length > 0) params.employee_profile_ids = selectedIds;
      const res = await getAbsencesUsageReport(params);
      setData(res?.data || res || null);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar el reporte.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedIds, includeBalance]);

  useEffect(() => {
    if (isManager) run();
    // eslint-disable-next-line
  }, [isManager]);

  const onExportCsv = () => {
    if (!data?.rows?.length) {
      toast.error("Nada que exportar — ejecuta el reporte primero.");
      return;
    }
    const csv = buildCsv(data);
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ausencias_${data.start_date}_a_${data.end_date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="secondary"
          icon="Download"
          onClick={onExportCsv}
          disabled={!data?.rows?.length}
        >
          Exportar CSV
        </Button>
      </div>

      <Card>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto auto",
            gap: 12,
            alignItems: "end",
          }}
        >
          <Field label="Desde">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
            />
          </Field>
          <Field label="Hasta">
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
            />
          </Field>
          <Button
            variant="ghost"
            onClick={() => {
              setStartDate(firstOfYear);
              setEndDate(today);
              setSelectedIds([]);
              setIncludeBalance(true);
            }}
          >
            Reiniciar
          </Button>
          <Button variant="primary" icon="Play" onClick={run} disabled={loading}>
            {loading ? "Generando…" : "Generar"}
          </Button>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field
            label="Empleados"
            help="Vacío = todos los empleados activos del workspace."
          >
            <EmployeeMultiPicker
              employees={employees}
              value={selectedIds}
              onChange={setSelectedIds}
            />
          </Field>
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 12,
            fontSize: 13,
            color: "var(--kr-fg-strong)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={includeBalance}
            onChange={(e) => setIncludeBalance(e.target.checked)}
          />
          Incluir balance de vacaciones vigente (más lento si hay muchos
          empleados)
        </label>
      </Card>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading && !data && (
        <Card>
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        </Card>
      )}

      {data && (
        <>
          <Card>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 16,
              }}
            >
              <Stat label="Empleados" value={data.summary?.employees ?? 0} />
              <Stat
                label="Total días"
                value={fmtDays(data.summary?.days_total)}
              />
              <Stat
                label="Vacaciones"
                value={fmtDays(data.summary?.days_by_type?.vacation)}
              />
              <Stat
                label="Personales"
                value={fmtDays(data.summary?.days_by_type?.personal)}
              />
              <Stat
                label="Baja"
                value={fmtDays(data.summary?.days_by_type?.sick)}
              />
              <Stat
                label="Otras"
                value={fmtDays(
                  (data.summary?.days_by_type?.parental || 0) +
                    (data.summary?.days_by_type?.other || 0),
                )}
              />
            </div>
          </Card>

          <Card padded={false}>
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid var(--kr-border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--kr-fg-strong)",
                }}
              >
                Detalle por empleado
              </div>
              <Badge tone="neutral">{data.rows?.length || 0} filas</Badge>
            </div>

            {(!data.rows || data.rows.length === 0) ? (
              <EmptyState
                icon="Calendar"
                title="Sin ausencias"
                description="No hay ausencias aprobadas en el rango seleccionado."
              />
            ) : (
              <AbsencesTable rows={data.rows} includeBalance={includeBalance} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default AbsencesReport;

function AbsencesTable({ rows, includeBalance }) {
  const cols = includeBalance
    ? "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1.4fr"
    : "2fr 1fr 1fr 1fr 1fr 1fr 1fr";
  const minWidth = includeBalance ? 980 : 800;
  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols,
          padding: "10px 20px",
          background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          color: "var(--kr-fg-muted)",
          minWidth,
        }}
      >
        <span>Empleado</span>
        <span>Vacaciones</span>
        <span>Personales</span>
        <span>Baja</span>
        <span>Parental</span>
        <span>Otras</span>
        <span>Total</span>
        {includeBalance && <span>Vacaciones disponibles</span>}
      </div>
      {rows.map((r, i) => (
        <div
          key={r.employee_profile_id}
          style={{
            display: "grid",
            gridTemplateColumns: cols,
            padding: "10px 20px",
            borderBottom:
              i === rows.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
            fontSize: 13,
            minWidth,
          }}
        >
          <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>
            {r.employee_name || "—"}
            {r.position && (
              <div
                style={{
                  color: "var(--kr-fg-muted)",
                  fontSize: 11,
                  marginTop: 2,
                }}
              >
                {r.position}
              </div>
            )}
          </div>
          {["vacation", "personal", "sick", "parental", "other"].map((t) => (
            <DaysCell key={t} value={r.days_by_type?.[t]} />
          ))}
          <div
            style={{
              fontFamily: "var(--kr-font-mono)",
              color: "var(--kr-fg-strong)",
              fontWeight: 700,
            }}
          >
            {fmtDays(r.days_total)}
          </div>
          {includeBalance && <BalanceCell balance={r.vacation_balance} />}
        </div>
      ))}
    </div>
  );
}

function DaysCell({ value }) {
  const n = Number(value) || 0;
  return (
    <div
      style={{
        fontFamily: "var(--kr-font-mono)",
        color: n > 0 ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
        fontWeight: n > 0 ? 600 : 400,
      }}
    >
      {n > 0 ? fmtDays(n) : "—"}
    </div>
  );
}

function BalanceCell({ balance }) {
  if (!balance) {
    return (
      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>—</div>
    );
  }
  if (balance.unlimited) {
    return <Badge tone="info">Ilimitado</Badge>;
  }
  const available = Number(balance.available) || 0;
  const allowance = Number(balance.allowance) || 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontWeight: 700,
          color:
            available > 0 ? "var(--kr-success-700)" : "var(--kr-warning-700)",
        }}
      >
        {fmtDays(available)}
      </span>
      <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
        de {fmtDays(allowance)}
      </span>
    </div>
  );
}

function fmtDays(n) {
  if (n === null || n === undefined) return "0 d";
  const num = Number(n) || 0;
  // Mostrar enteros sin decimales, fraccionarios con 1 decimal.
  const rounded = Math.round(num * 10) / 10;
  const txt = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${txt} d`;
}

function buildCsv(data) {
  const header = [
    "employee_profile_id",
    "employee_name",
    "position",
    "vacation_days",
    "personal_days",
    "sick_days",
    "parental_days",
    "other_days",
    "total_days",
    "vacation_balance_available",
    "vacation_balance_allowance",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(",") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [header.join(",")];
  for (const r of data.rows || []) {
    lines.push(
      [
        escape(r.employee_profile_id),
        escape(r.employee_name),
        escape(r.position),
        escape(r.days_by_type?.vacation || 0),
        escape(r.days_by_type?.personal || 0),
        escape(r.days_by_type?.sick || 0),
        escape(r.days_by_type?.parental || 0),
        escape(r.days_by_type?.other || 0),
        escape(r.days_total || 0),
        escape(r.vacation_balance?.available ?? ""),
        escape(r.vacation_balance?.allowance ?? ""),
      ].join(","),
    );
  }
  return lines.join("\n");
}
